import { useState, useEffect, useRef } from "react";
import { Folder, FileText } from "lucide-react";
import { usePentagovContext } from "../../context/pentagovContext";
import { useOperador } from "../../Hooks/useOperador";
import { FaPencilAlt } from "react-icons/fa";
import { BiRename } from "react-icons/bi";
import { TiDelete } from "react-icons/ti";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import { excluirDiretorioRecursivamente } from "../../Services/explorerExclusaoRecursiva";
import { arquivoService } from "../../Services/arquivo";
import { diretorioService } from "../../Services/diretorio";
import { EArquivoSituacao, EDiretorioSituacao } from "../../Enum/Enum";
import api from "../../Services/api";
import "bootstrap/dist/css/bootstrap.min.css";
import "./stylepasta.css";

const MySwal = withReactContent(Swal);

const FileExplorer = ({ diretorios, arquivos, carregarConteudo, abrirArquivo, IDProcesso, pastaAtual }) => {
  const containerRef = useRef();
  const { objPastaAberta } = usePentagovContext();

  const [pastaAberta, setPastaAberta] = useState(null);
  const [itemComMenuAberto, setItemComMenuAberto] = useState({ id: null, tipo: null });
  const [fadeOut, setFadeOut] = useState(false);
  const [loading, setLoading] = useState(false);
  const [filtroGlobal, setFiltroGlobal] = useState("");
  const { IDOperador } = useOperador();

  const listaFiltrada = [
    ...diretorios
      .filter((pasta) => pasta.situacao === EDiretorioSituacao.ATIVO)
      .map((pasta) => ({ ...pasta, tipo: "pasta", nomeExibicao: pasta.descricaoExibicao })),

    ...arquivos
      .filter((arquivo) => arquivo.situacao === EArquivoSituacao.ATIVO)
      .map((arquivo) => ({ ...arquivo, tipo: "arquivo", nomeExibicao: arquivo.nome })),
  ].filter((item) =>
    item.nomeExibicao?.toLowerCase().includes(filtroGlobal.toLowerCase())
  )
    .sort((a, b) => {
      if (a.tipo === "pasta" && b.tipo !== "pasta") return -1;
      if (a.tipo !== "pasta" && b.tipo === "pasta") return 1;

      return a.nomeExibicao.localeCompare(b.nomeExibicao);
    });

  useEffect(() => {
    console.log('Arquivos', arquivos);
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setItemComMenuAberto({ id: null, tipo: null });
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const toggleMenu = (id, tipo) => {
    setItemComMenuAberto((prev) =>
      prev.id === id && prev.tipo === tipo ? { id: null, tipo: null } : { id, tipo }
    );
  };

  async function inserirAuditoria(dados) {
    try {
      const response = await api.post("/sis_auditoria", dados);
      return response.data;
    } catch (error) {
      console.error("Erro ao inserir auditoria:", error.response?.data || error);
      throw error;
    }
  }

  const handleClickPasta = (id) => {
    setPastaAberta(id === pastaAberta ? null : id);
    carregarConteudo(id, IDProcesso);
  };

  const handleRenomear = async (item, tipo) => {
    const { value: novoNome } = await MySwal.fire({
      title: `Renomear ${tipo === "pasta" ? "pasta" : "arquivo"}`,
      input: "text",
      inputLabel: "Digite o novo nome:",
      inputValue: item.descricaoExibicao || item.nome,
      inputAttributes: {
        maxlength: 800,
      },
      icon: "info",
      showCancelButton: true,
      confirmButtonText: "Atualizar",
      confirmButtonColor: "green",
      cancelButtonText: "Voltar",
      cancelButtonColor: "var(--color-btnCancelar-danger)",
      inputValidator: (value) => {
        if (!value || value.trim() === "") return "O nome não pode estar vazio.";
        if (value === (item.descricaoExibicao || item.nome)) return "Digite um nome diferente.";
        return null;
      },
      customClass: {
        input: "swal-input-limitado",
        popup: "swal-popup-fix",
      },
    });

    if (!novoNome) return;

    try {
      setLoading(true);
      if (tipo === "pasta") {
        const res = await diretorioService.renomearDiretorio(item.IDDiretorio, novoNome);
        if (res?.status !== 204) {
          console.error("Erro ao renomear diretório:", res);
          setLoading(false);
          return;
        }
        //Gravar Auditoria Renomear
        const dados = {
          tipo: 1,
          nomearquivo: "",
          nomediretorio: novoNome,
          IDarquivo: 0,
          IDdiretorio: item.IDDiretorio,
          texto: "Renomeou o nome do diretorio.",
          IDOperador: IDOperador,
          Data: formatarDataAgora(),
        };
        await inserirAuditoria(dados);

      } else if (tipo === "arquivo") {
        const res = await arquivoService.renomearArquivo(item.IDArquivo, novoNome);
        if (res?.status !== 204) {
          console.error("Erro ao renomear arquivo:", res);
          setLoading(false);
          return;
        }
        //Gravar Auditoria Renomear
        const dados = {
          tipo: 1,
          nomearquivo: novoNome,
          nomediretorio: "",
          IDarquivo: item.IDArquivo,
          IDdiretorio: 0,
          texto: "Renomeou o nome do arquivo.",
          IDOperador: IDOperador,
          Data: formatarDataAgora(),
        };
        await inserirAuditoria(dados);
      }

      await carregarConteudo(pastaAtual, IDProcesso);

    } catch (error) {
      console.error("Erro ao renomear:", error);
      MySwal.fire("Erro", "Falha ao renomear. Verifique sua conexão ou tente novamente.", "error");
    } finally {
      setLoading(false);
    }
  };

  function formatarDataAgora() {
    const data = new Date();

    const ano = data.getFullYear();
    const mes = String(data.getMonth() + 1).padStart(2, '0');
    const dia = String(data.getDate()).padStart(2, '0');

    const hora = String(data.getHours()).padStart(2, '0');
    const min = String(data.getMinutes()).padStart(2, '0');
    const seg = String(data.getSeconds()).padStart(2, '0');

    return `${ano}/${mes}/${dia} ${hora}:${min}:${seg}`;
  }

  const handleExcluir = async (item, tipo) => {
    const { value: confirmacao } = await MySwal.fire({
      title: `Excluir ${tipo === "pasta" ? "pasta" : "arquivo"}?`,
      html: `
      <p>Atenção! Esta ação é <strong>irreversível</strong>.</p>
      ${tipo === "pasta" ? `<p>Todos os arquivos dentro desta pasta serão deletados.</p>` : ""}
      <p>Para prosseguir com a exclusão, digite <strong>CONFIRMAR</strong> abaixo:</p>
    `,
      input: "text",
      inputPlaceholder: "Digite 'confirmar'",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Excluir",
      confirmButtonColor: "red",
      cancelButtonText: "Voltar",
      cancelButtonColor: "var(--color-btnCancelar-danger)",
      inputValidator: (value) =>
        value !== "confirmar" ? "Você precisa digitar 'confirmar' para prosseguir." : null,
      customClass: {
        input: "swal-input-limitado",
        popup: "swal-popup-fix",
      },
    });

    if (confirmacao === "confirmar") {
      setLoading(true);
      try {
        if (tipo === "pasta") {
          const sucesso = await excluirDiretorioRecursivamente(item.IDDiretorio, IDProcesso);
          if (!sucesso) {
            console.error("Erro ao excluir diretório de forma recursiva");
            setLoading(false);
            return;
          }
          //Gravar Auditoria Deletar
          const dados = {
            tipo: 2,
            nomearquivo: "",
            nomediretorio: item.descricao,
            IDarquivo: 0,
            IDdiretorio: item.IDDiretorio,
            texto: "Deletou pasta.",
            IDOperador: IDOperador,
            Data: formatarDataAgora(),
          };
          await inserirAuditoria(dados);
        }
        if (tipo === "arquivo") {
          const res = await atualizarSituacaoArquivo(item.IDArquivo, EArquivoSituacao.INATIVO);
          if (res?.status !== 204) {
            console.error("Falha ao atualizar situação do arquivo");
            setLoading(false);
            return;
          }
          //Gravar Auditoria Deletar
          const dados = {
            tipo: 2,
            nomearquivo: item.nome,
            nomediretorio: "",
            IDarquivo: item.IDArquivo,
            IDdiretorio: 0,
            texto: "Deletou arquivo.",
            IDOperador: IDOperador,
            Data: formatarDataAgora(),
          };
          await inserirAuditoria(dados);
        }
        await carregarConteudo(pastaAtual, IDProcesso);

      } catch (error) {
        console.error("Erro ao excluir", error);
      } finally {
        setLoading(false);
      }
    }
  };

  const atualizarSituacaoArquivo = async (IDArquivo, situacao) => {
    try {
      const res = await arquivoService.atualizarSituacaoArquivo(IDArquivo, situacao);
      return res;
    } catch (error) {
      console.log("Falha ao atualizar situação do arquivo", error);
      return null;
    }
  };

  function convertUTF8(texto) {
    try {
      return decodeURIComponent(escape(texto));
    } catch {
      return texto;
    }
  }

  return (
    <>
      {loading && (
        <div className="loading-overlay">
          <div className="spinner-border text-light" role="status">
            <span className="visually-hidden">Carregando...</span>
          </div>
        </div>
      )}

      <div ref={containerRef} className="file-explorer-container container my-4">
        <div className="d-flex align-items-center mb-3" style={{ position: "relative" }}>
          <div style={{ flex: "1 1", minWidth: 0, textAlign: "left" }}>
            {objPastaAberta && (
              <div className="row">
                <div className="col-1" style={{ textAlign: "center", alignContent: 'center', paddingRight: 0 }}>
                  <Folder style={{ height: "1.3em", width: "1.3em", color: '#444' }} />
                </div>
                <div className="col-11" style={{ textAlign: "left", alignContent: 'end', paddingLeft: 0 }}>
                  <p className="text-truncate" style={{ maxWidth: "40ch", marginBottom: 0, fontSize: 12, color: '#444' }}>
                    {objPastaAberta.descricaoExibicao}
                  </p>
                </div>
              </div>
            )}
          </div>
          <div style={{ position: "absolute", left: "50%", transform: "translateX(-50%)", marginBottom: 10 }}>
            <h3 className="fw-bold text-center mb-0">Explorador de Arquivos</h3>
          </div>
          <div style={{ flex: "1 1" }} />
        </div>


        <input
          type="text"
          placeholder="Filtrar arquivos e pastas..."
          style={{ fontSize: 13, paddingLeft: 10, marginBottom: 10, background: '#fafafa', color: '#555' }}
          value={filtroGlobal}
          onChange={(e) => setFiltroGlobal(e.target.value)}
          onBlur={() => setTimeout(() => setFiltroGlobal(""), 300)}
          className={`input-filtro ${listaFiltrada.length > 0 ? "show" : ""}`}
        />


        {/* LISTA DE PASTAS */}
        {listaFiltrada.map((item) =>
          item.tipo === "pasta" ? (
            <div
              key={`pasta-${item.IDDiretorio}`}
              className="list-item d-flex align-items-center p-2 mb-1 rounded hover-bg"
              style={{ position: "relative" }}
              onClick={() => handleClickPasta(item.IDDiretorio)}
              onMouseLeave={() => {
                setFadeOut(true);
                setTimeout(() => {
                  setItemComMenuAberto({ id: null, tipo: null });
                  setFadeOut(false);
                }, 200);
              }}
            >
              <Folder className="me-2 text-primary" />
              <span className="text-truncate ms-1">{convertUTF8(item.nomeExibicao)}</span>
              <button
                className="btn-opcoes ms-auto"
                onClick={(e) => {
                  e.stopPropagation();
                  toggleMenu(item.IDDiretorio, "pasta");
                }}
              >
                <FaPencilAlt style={{ color: "#fff", fontSize: "16px" }} />
              </button>

              {itemComMenuAberto.id === item.IDDiretorio && itemComMenuAberto.tipo === "pasta" && (
                <div
                  className={`submenu-arquivo ${fadeOut ? "fade-out" : "fade-in"}`}
                  style={{ position: "absolute", top: "100%", left: "0" }}
                >
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRenomear(item, "pasta");
                    }}
                  >
                    <BiRename size={16} style={{ marginRight: 2 }} />
                    Renomear
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleExcluir(item, "pasta");
                    }}
                  >
                    <TiDelete size={16} style={{ marginRight: 2 }} />
                    Excluir
                  </button>
                </div>
              )}
            </div>
          ) :
            /* LISTA DE ARQUIVOS */
            (
              <div
                key={`arquivo-${item.IDArquivo}`}
                className="list-item d-flex align-items-center p-2 mb-1 rounded hover-bg"
                style={{ position: "relative" }}
                onClick={() => abrirArquivo(item.IDArquivo)}
                onMouseLeave={() => {
                  setFadeOut(true);
                  setTimeout(() => {
                    setItemComMenuAberto({ id: null, tipo: null });
                    setFadeOut(false);
                  }, 200);
                }}
              >
                <FileText className="me-2 text-secondary" />
                <span className="text-truncate ms-1">{convertUTF8(item.nomeExibicao)}</span>

                <div className="d-flex align-items-center ms-auto" style={{ gap: 8 }}>
                  <span className="text-truncate ms-1 me-1" style={{ fontSize: 10 }}>
                    Modificado em: {new Date(item.dataAlteracao).toLocaleString("pt-BR")}
                  </span>
                  <button
                    className="btn-opcoes ms-auto"
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleMenu(item.IDArquivo, "arquivo");
                    }}
                  >
                    <FaPencilAlt style={{ color: "#fff", fontSize: "16px" }} />
                  </button>
                </div>

                {itemComMenuAberto.id === item.IDArquivo && itemComMenuAberto.tipo === "arquivo" && (
                  <div
                    className={`submenu-arquivo ${fadeOut ? "fade-out" : "fade-in"}`}
                    style={{ position: "absolute", top: "100%", left: "0" }}
                  >
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRenomear(item, "arquivo");
                      }}
                    >
                      <BiRename size={16} style={{ marginRight: 2 }} />
                      Renomear
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleExcluir(item, "arquivo");
                      }}
                    >
                      <TiDelete size={16} style={{ marginRight: 2 }} />
                      Excluir
                    </button>
                  </div>
                )}
              </div>
            )
        )}

      </div>
    </>
  );
};

export default FileExplorer;
